<template>
  <ul
    v-if="items && items.length > 1"
    v-show="show"
    class="vch-carousel-indicator text-small"
    :class="`vch-carousel-indicator--${indicatorType}`"
    :aria-label="ariaPaginationLabel"
  >
    <li
      v-for="({ id }, index) in items"
      :key="id"
      class="vch-carousel-indicator__item"
    >
      <a
        v-if="useAnchorScrolling"
        :href="`#${id}`"
        :class="[
          'vch-carousel-indicator__link',
          Math.abs(activeIndicator) === Math.abs(parseInt(index)) && '--active',
        ]"
        @click="pauseVideo"
      >
        <span>{{ prefixNumber(Math.abs(parseInt(index))) }}</span>
      </a>
      <button
        v-else
        :class="[
          'vch-carousel-indicator__link',
          Math.abs(activeIndicator) === Math.abs(parseInt(index)) && '--active',
        ]"
        :aria-current="Math.abs(activeIndicator) === Math.abs(parseInt(index))"
        @click="() => onIndicatorButtonClick(Math.abs(parseInt(index)))"
        @keyup.enter="onIndicatorButtonClick(Math.abs(parseInt(index)))"
      >
        <span>{{ prefixNumber(Math.abs(parseInt(index))) + ' - ' + ariaItemLabel }}</span>
      </button>
    </li>
  </ul>
</template>

<script>
let observer;
const indicatorTypes = ['numbers', 'dots', 'bar'];

export default {
  name: 'vch-carousel-indicator',

  props: {
    indicatorType: {
      type: String,
      required: false,
      default: indicatorTypes[0],
      validator: (value) => {
        return !!value && indicatorTypes.includes(value);
      },
    },
    itemSelector: {
      type: String,
      required: true,
    },
    mediaQueryList: {
      type: String,
      default: '(min-width: 0px)',
    },
    ariaPaginationLabel: {
      type: String,
      default: 'Pagination',
    },
    ariaItemLabel: {
      type: String,
      default: 'Picture',
    },
    intersectionThreshold: {
      type: Number,
      default: 0.9,
      required: false,
    },
    observeContent: {
      type: Boolean,
      default: true,
      required: false,
    },
    currentItem: {
      type: Number,
      required: false,
      default: 0,
    },
    useAnchorScrolling: {
      type: Boolean,
      default: true,
      required: false,
    },
    onIndicatorClick: {
      type: Function,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      show: false,
      items: null,
      intersectingItemIndex: 0,
      mql: null,
    };
  },
  computed: {
    activeIndicator() {
      window.eventBus.$emit('vch-remove-zoom-icon', this.intersectingItemIndex);
      return this.observeContent
        ? parseInt(this.intersectingItemIndex)
        : this.currentItem;
    },
  },
  mounted() {
    this.items = this.getItems();

    if (this.items.length >= 1) {
      this.mql = window.matchMedia(this.mediaQueryList);
      if (this.observeContent) {
        observer = new IntersectionObserver(this.updateIntersectingItemIndex, {
          root: document.body,
          threshold: this.intersectionThreshold,
        });
      }
      this.toggleIndicator(this.mql);
      this.mql.addEventListener('change', this.toggleIndicator);
    }
  },
  beforeDestroy() {
    if (this.observeContent) {
      this.stopObserver();
    }
    if (this.mql) {
      this.mql.removeEventListener('change', this.toggleIndicator);
    }
  },
  methods: {
    getItems() {
      const parentElement = this.$el.parentElement;
      if (parentElement) {
        return Array.from(
          parentElement.querySelectorAll(this.itemSelector)
        ).filter((el) =>
          el.hasAttribute('hidden')
            ? !JSON.parse(el.getAttribute('hidden'))
            : true
        );
      }
      return document.querySelectorAll(this.itemSelector);
    },

    toggleIndicator({ matches }) {
      this.show = matches;
      if (this.observeContent) {
        if (this.show) {
          this.startObserver();
        } else {
          this.stopObserver();
        }
      }
    },

    updateIntersectingItemIndex([e]) {
      if (e.isIntersecting) {
        this.intersectingItemIndex = Array.prototype.indexOf
          .call(this.items, e.target)
          .toString();
      }
    },

    startObserver() {
      this.items.forEach((item) => {
        observer.observe(item);
      });
    },

    stopObserver() {
      this.items.forEach((item) => {
        observer.unobserve(item);
      });
    },

    prefixNumber(num) {
      return num < 10 ? `0${num + 1}` : `${num + 1}`;
    },

    onIndicatorButtonClick(index) {
      if (this.onIndicatorClick) {
        this.onIndicatorClick(index);
      }
    },

    pauseVideo() {
      let iframes = document.querySelectorAll('.youtube-player');
      if (iframes) {
        for (var i = 0; i < iframes.length; i++) {
          var iframe = iframes[i];
          var temp = iframe.src;
          iframe.src = temp;
        }
      }
    },
  },
};
</script>

<style lang="scss" src="./carousel-indicator.scss"></style>
